import Builder from 'react-native-jala-orm/src/model/Builder';
import QueryBuilder from 'react-native-jala-orm/src/query/Builder';
import _ from 'lodash';
import Expression from 'react-native-jala-orm/src/query/Expression';
import moment from 'moment';
import SyncableModel from './SyncableModel';
import Cycle from './Cycle';
import Fasting from './Fasting';
import Farm from './Farm';
import Pond from './Pond';

export default class Feed extends SyncableModel {
    static name = 'Feed'

    _fillable = [
      'id', 'cycle_id', 'stock_id', 'quantity', 'logged_at', 'remark',
      'created_at', 'updated_at', 'deleted_at', 'synced_at',
    ]

    static _parents = ['cycle'];

    static endpoint = '/feeds'

    static allowReplaceUpdate = true

    _casts = {
      'fasting': 'boolean'
    }

    static _boot() {
      super._boot();

      this.addGlobalScope('global', (query: Builder) => {
        if (_.isEmpty(query.getQuery().columns)) {
          query.select([query.qualifyColumn('*')]);
        }

        const cycle = (new Cycle()).setTable(`${query.getModel().getTable()}_cycles`);
        query.leftJoin(
          `${(new Cycle()).getTable()} as ${cycle.getTable()}`,
          cycle.getQualifiedKeyName(), query.qualifyColumn(cycle.getForeignKey())
        );

        const pond = (new Pond()).setTable(`${query.getModel().getTable()}_ponds`);
        query.leftJoin(
          `${(new Pond()).getTable()} as ${pond.getTable()}`,
          pond.getQualifiedKeyName(), cycle.qualifyColumn(pond.getForeignKey())
        );

        const farm = (new Farm()).setTable(`${query.getModel().getTable()}_farms`);
        query.leftJoin(
          `${(new Farm()).getTable()} as ${farm.getTable()}`,
          farm.getQualifiedKeyName(), pond.qualifyColumn(farm.getForeignKey())
        );

        const fasting = (new Fasting()).setTable(`${query.getModel().getTable()}_fastings`);
        query.leftJoin(`${(new Fasting()).getTable()} as ${fasting.getTable()}`, (q1: QueryBuilder) => {
          q1.whereColumn(query.qualifyColumn('cycle_id'), fasting.qualifyColumn('cycle_id'))
            .whereColumn(query.qualifyColumn('feed_at'), fasting.qualifyColumn('logged_date'));
        });
      });
    }

    qualifyColumn(column: string): string {
      if (column === '*') {
        return new Expression(
          `${this.getTable()}.*, `
            + `${this.qualifyColumn('feed_at')} as feed_at, `
            + `${this.qualifyColumn('logged_date')} as logged_date, `
            + `${this.qualifyColumn('logged_time')} as logged_time, `
            + `${this.qualifyColumn('accumulation')} as accumulation, `
            + `${this.qualifyColumn('age')} as age, `
            + `${this.qualifyColumn('fasting')} as fasting`
        );
      }
      if (column === 'feed_at') {
        return new Expression(`DATE(${this.qualifyColumn('logged_at')}, ${this.qualifyColumn('timezone')})`);
      }
      if (column === 'logged_date') {
        return new Expression(`DATE(${this.qualifyColumn('logged_at')})`);
      }
      if (column === 'logged_time') {
        return new Expression(`TIME(${this.qualifyColumn('logged_at')})`);
      }
      if (column === 'accumulation') {
        const feed = (new Feed()).setTable(_.uniqueId('t'));
        const feedQuery = Feed.query().withoutGlobalScopes()
          .selectRaw(`SUM(${feed.qualifyColumn('quantity')})`)
          .table(this.getTable(), feed.getTable())
          .whereColumn(feed.qualifyColumn('cycle_id'), this.qualifyColumn('cycle_id'))
          .where((query: Builder) => {
            query.whereColumn(feed.qualifyColumn('logged_at'), '<', this.qualifyColumn('logged_at'))
              .orWhere((q1: Builder) => {
                q1.whereColumn(feed.qualifyColumn('logged_at'), this.qualifyColumn('logged_at'))
                  .whereColumn(feed.qualifyColumn('id'), '<', this.qualifyColumn('id'));
              })
              .orWhereColumn(feed.qualifyColumn('id'), this.qualifyColumn('id'));
          });
        return new Expression(`(${feedQuery.toSql()})`);
      }
      if (column === 'age') {
        const cycle = (new Cycle()).setTable(`${this.getTable()}_cycles`);
        return new Expression(
          '('
            + 'CAST('
              + `(JULIANDAY(${this.qualifyColumn('feed_at')}) - `
              + `JULIANDAY(${cycle.qualifyColumn('started_at')})) AS INTEGER`
            + `) + COALESCE(${cycle.qualifyColumn('initial_age')}, 0)`
          + ')'
        );
      }
      if (column === 'fasting') {
        const fasting = (new Fasting()).setTable(`${this.getTable()}_fastings`);
        return fasting.qualifyColumn(column);
      }
      if (column === 'timezone') {
        const timezone = moment().format('Z');
        const farm = (new Farm()).setTable(`${this.getTable()}_farms`);
        return new Expression(`COALESCE(${farm.qualifyColumn('timezone')}, '${timezone}')`);
      }
      return super.qualifyColumn(column);
    }

    /**
     * @param {String} value
     * @return {String}
     */
    getFeedAtAttribute(value) {
      if (value) return value;
      if (this.exists) {
        this._attributes.feed_at = this._attributes.logged_at ? moment.utc(this._attributes.logged_at).local().format('YYYY-MM-DD') : null;
        this.syncOriginalAttribute('feed_at');
        return this._attributes.feed_at;
      }

      return this._attributes.logged_at ? moment.utc(this._attributes.logged_at).local().format('YYYY-MM-DD') : null;
    }

    /**
     * @param {String} value
     * @return {String}
     */
    getLoggedDateAttribute(value) {
      if (value) return value;
      if (this.exists) {
        this._attributes.logged_date = this._attributes.logged_at ? moment.utc(this._attributes.logged_at).format('YYYY-MM-DD') : null;
        this.syncOriginalAttribute('logged_date');
        return this._attributes.logged_date;
      }

      return this._attributes.logged_at ? moment.utc(this._attributes.logged_at).format('YYYY-MM-DD') : null;
    }

    /**
     * @param {String} value
     */
    setLoggedDateAttribute(value) {
      const date = moment.utc(value || undefined, 'YYYY-MM-DD', false);
      this._attributes = {
        ...this._attributes,
        logged_at: date.format('YYYY-MM-DD') + moment.utc(this._attributes.logged_at || undefined).format(' HH:mm:ss')
      };
      if (this.exists) {
        this._attributes = {
          ...this._attributes,
          logged_date: date.format('YYYY-MM-DD')
        };
        this.syncOriginalAttribute('logged_date');
      }
      return this;
    }

    /**
     * @param {String} value
     * @return {String}
     */
    getLoggedTimeAttribute(value) {
      if (value) return value;
      if (this.exists) {
        this._attributes.logged_time = this._attributes.logged_at ? moment.utc(this._attributes.logged_at).format('HH:mm:ss') : null;
        this.syncOriginalAttribute('logged_time');
        return this._attributes.logged_time;
      }

      return this._attributes.logged_at ? moment.utc(this._attributes.logged_at).format('HH:mm:ss') : null;
    }

    /**
     * @param {String} value
     */
    setLoggedTimeAttribute(value) {
      const time = moment.utc(value || undefined, 'HH:mm:ss', false);
      this._attributes = {
        ...this._attributes,
        logged_at: moment.utc(this._attributes.logged_at || undefined).format('YYYY-MM-DD') + time.format(' HH:mm:ss')
      };
      if (this.exists) {
        this._attributes = {
          ...this._attributes,
          logged_time: time.format('HH:mm:ss')
        };
        this.syncOriginalAttribute('logged_time');
      }
      return this;
    }

    relationCycle() {
      return Cycle.query().withoutGlobalScope('soft_delete').whereKey(this.cycle_id);
    }
}
